import { useMemo } from "react";

import {
  Badge,
  CardInfo,
  FontIcon,
  ResponsiveGridLayout,
  ResponsiveGridLayouts,
} from "@ezmid/components-library";
import {
  faUsers,
  faMoneyCheck,
  faChalkboardTeacher,
  faBook,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import {
  useGetBackOfficeListCoursesQuery,
  useGetBackOfficeListPaymentsQuery,
} from "../../../api";
import { useGetBackOfficeListLecturersQuery } from "../../../api/lecturers.generated";
import { useGetBackOfficeListUsersQuery } from "../../../api/users.generated";
import { PaymentsTable } from "../../../components/tables/PaymentsTable";
import { UsersTable } from "../../../components/tables/UsersTable";
import { useFormatters } from "../../../hooks";
import { PageLoader } from "../../loader/PageLoader";

type Props = {
  setLayouts: (layouts: ResponsiveGridLayouts) => void;
  layouts: ResponsiveGridLayouts;
};

export function DashboardPageContent({ setLayouts, layouts }: Props) {
  const { t } = useTranslation();
  const { formatTime } = useFormatters();

  const {
    currentData: users,
    isFetching: areUsersFetching,
    fulfilledTimeStamp,
  } = useGetBackOfficeListUsersQuery(
    {
      page: 1,
      size: 10,
    },
    {
      pollingInterval: 60000,
    }
  );
  const {
    currentData: payments,
    isFetching: arePaymentsFetching,
    fulfilledTimeStamp: fulfilledTimeStampPayments,
  } = useGetBackOfficeListPaymentsQuery(
    {
      page: 1,
      size: 10,
    },
    {
      pollingInterval: 60000,
    }
  );
  const {
    currentData: lecturers,
    isFetching: areLecturersFetching,
    fulfilledTimeStamp: fulfilledTimeStampLecturers,
  } = useGetBackOfficeListLecturersQuery(
    {
      page: 1,
      size: 10,
    },
    {
      pollingInterval: 60000,
    }
  );

  const {
    currentData: courses,
    isFetching: areCoursesFetching,
    fulfilledTimeStamp: fulfilledTimeStampCourses,
  } = useGetBackOfficeListCoursesQuery(
    {
      page: 1,
      size: 1,
    },
    {
      pollingInterval: 60000,
    }
  );

  const usersItems = useMemo(() => users?.items || [], [users?.items]);
  const paymentsItems = useMemo(() => payments?.items || [], [payments?.items]);

  const widgetArray = [
    {
      key: "count_users",
      widget: (
        <CardInfo
          color="blue"
          title={users?.count?.toString() ?? "0"}
          description={t("dashboard.card.count.users") as string}
          hasBorder={false}
          icon={
            <FontIcon
              marginRight={28}
              marginBottom={24}
              customIcon={
                <FontIcon
                  customIcon={
                    <FontAwesomeIcon size="4x" color="#0066F8" icon={faUsers} />
                  }
                />
              }
            />
          }
          badge={
            <Badge
              variant="pill"
              fill="outline"
              leftIcon={
                <FontIcon
                  marginTop={5}
                  marginLeft={4}
                  customIcon={<FontAwesomeIcon icon={faSyncAlt} />}
                />
              }
              status="primary"
            >
              {formatTime(new Date(fulfilledTimeStamp ?? "0").toUTCString())}
            </Badge>
          }
        />
      ),
    },
    {
      key: "count_payments",
      widget: (
        <CardInfo
          color="blue"
          title={payments?.count?.toString() ?? "0"}
          description={t("dashboard.card.count.payments") as string}
          hasBorder={false}
          icon={
            <FontIcon
              marginRight={40}
              marginBottom={24}
              customIcon={
                <FontIcon
                  customIcon={
                    <FontAwesomeIcon
                      size="4x"
                      color="#0066F8"
                      icon={faMoneyCheck}
                    />
                  }
                />
              }
            />
          }
          badge={
            <Badge
              variant="pill"
              fill="outline"
              leftIcon={
                <FontIcon
                  marginTop={5}
                  marginLeft={4}
                  customIcon={<FontAwesomeIcon icon={faSyncAlt} />}
                />
              }
              status="primary"
            >
              {formatTime(
                new Date(fulfilledTimeStampPayments ?? "0").toUTCString()
              )}
            </Badge>
          }
        />
      ),
    },
    {
      key: "count_lecturers",
      widget: (
        <CardInfo
          color="blue"
          title={lecturers?.count?.toString() ?? "0"}
          description={t("dashboard.card.count.lecturers") as string}
          hasBorder={false}
          icon={
            <FontIcon
              marginRight={40}
              marginBottom={24}
              customIcon={
                <FontIcon
                  customIcon={
                    <FontAwesomeIcon
                      size="4x"
                      color="#0066F8"
                      icon={faChalkboardTeacher}
                    />
                  }
                />
              }
            />
          }
          badge={
            <Badge
              variant="pill"
              fill="outline"
              leftIcon={
                <FontIcon
                  marginTop={5}
                  marginLeft={4}
                  customIcon={<FontAwesomeIcon icon={faSyncAlt} />}
                />
              }
              status="primary"
            >
              {formatTime(
                new Date(fulfilledTimeStampLecturers ?? "0").toUTCString()
              )}
            </Badge>
          }
        />
      ),
    },
    {
      key: "count_courses",
      widget: (
        <CardInfo
          color="blue"
          title={courses?.count?.toString() ?? "0"}
          description={t("dashboard.card.count.courses") as string}
          hasBorder={false}
          icon={
            <FontIcon
              marginRight={40}
              marginBottom={24}
              customIcon={
                <FontIcon
                  customIcon={
                    <FontAwesomeIcon size="4x" color="#0066F8" icon={faBook} />
                  }
                />
              }
            />
          }
          badge={
            <Badge
              variant="pill"
              fill="outline"
              leftIcon={
                <FontIcon
                  marginTop={5}
                  marginLeft={4}
                  customIcon={<FontAwesomeIcon icon={faSyncAlt} />}
                />
              }
              status="primary"
            >
              {formatTime(
                new Date(fulfilledTimeStampCourses ?? "0").toUTCString()
              )}
            </Badge>
          }
        />
      ),
    },
    {
      key: "users_table",
      widget: (
        <UsersTable
          isFetching={areUsersFetching}
          users={usersItems}
          headerText={t("dashboard.card.count.users")}
        />
      ),
    },
    {
      key: "payments_table",
      widget: (
        <PaymentsTable
          isFetching={arePaymentsFetching}
          payments={paymentsItems}
        />
      ),
    },
  ];

  const isLoading =
    areCoursesFetching ||
    areLecturersFetching ||
    arePaymentsFetching ||
    areUsersFetching;

  return isLoading ? (
    <PageLoader />
  ) : (
    <div className="w-full min-full">
      <ResponsiveGridLayout
        widgetArray={widgetArray}
        setLayouts={setLayouts}
        layouts={layouts}
      />
    </div>
  );
}
